import { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useClickOutsideOrEscape } from '@/hooks/useClickOutside';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle, X, Send, Plus, ArrowLeft, User, Shield } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

const statusLabels: Record<string, string> = {
  open: 'Aperto', in_progress: 'In corso', resolved: 'Risolto', closed: 'Chiuso',
};

export function SupportWidget() {
  const { user } = useAuth();
  const location = useLocation();
  const hideWidget = location.pathname === '/dashboard/new';
  const [isOpen, setIsOpen] = useState(false);
  const [view, setView] = useState<'list' | 'chat' | 'new'>('list');
  const [tickets, setTickets] = useState<any[]>([]);
  const [selectedTicket, setSelectedTicket] = useState<any>(null);
  const [messages, setMessages] = useState<any[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [subject, setSubject] = useState('');
  const [firstMessage, setFirstMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(false);
  const [unread, setUnread] = useState(0);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const toggleBtnRef = useRef<HTMLButtonElement>(null);

  const closePanel = useCallback(() => {
    setIsOpen(false);
  }, []);

  const panelRef = useClickOutsideOrEscape<HTMLDivElement>(() => {
    // Don't close if click was on the toggle button
    closePanel();
  }, isOpen);

  const fetchTickets = async () => {
    if (!user) return;
    setLoading(true);
    const { data } = await supabase
      .from('support_tickets')
      .select('*')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false });
    setTickets(data || []);
    setLoading(false);
  };

  const fetchMessages = async (ticketId: string) => {
    const { data } = await supabase
      .from('ticket_messages')
      .select('*')
      .eq('ticket_id', ticketId)
      .order('created_at', { ascending: true });
    setMessages(data || []);
  };

  useEffect(() => {
    if (isOpen && user) fetchTickets();
  }, [isOpen, user]);

  useEffect(() => {
    if (selectedTicket) {
      fetchMessages(selectedTicket.id);

      const channel = supabase
        .channel(`user-ticket-${selectedTicket.id}`)
        .on('postgres_changes', {
          event: 'INSERT',
          schema: 'public',
          table: 'ticket_messages',
          filter: `ticket_id=eq.${selectedTicket.id}`,
        }, (payload) => {
          setMessages((prev) => [...prev, payload.new]);
        })
        .subscribe();

      return () => { supabase.removeChannel(channel); };
    }
  }, [selectedTicket]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Listen for admin replies across all user's tickets
  useEffect(() => {
    if (!user) return;

    const channel = supabase
      .channel('support-notifications')
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'ticket_messages',
      }, (payload: any) => {
        const msg = payload.new;
        if (!msg?.is_admin) return;
        // Only count if widget is closed OR user is not viewing that specific ticket
        const isViewingThisTicket = isOpen && view === 'chat' && selectedTicket?.id === msg.ticket_id;
        if (!isViewingThisTicket) {
          setUnread(u => u + 1);
        }
      })
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [user, isOpen, view, selectedTicket]);

  if (!user || hideWidget) return null;

  const handleCreateTicket = async () => {
    if (!subject.trim() || !firstMessage.trim()) return;
    setSending(true);
    try {
      // Get org id
      const { data: member } = await supabase
        .from('organization_members')
        .select('organization_id')
        .eq('user_id', user.id)
        .limit(1)
        .maybeSingle();

      const { data: ticket, error } = await supabase
        .from('support_tickets')
        .insert({
          user_id: user.id,
          organization_id: member?.organization_id || null,
          subject: subject.trim(),
        })
        .select()
        .single();

      if (error) throw error;

      // Add first message
      await supabase.from('ticket_messages').insert({
        ticket_id: ticket.id,
        sender_id: user.id,
        is_admin: false,
        content: firstMessage.trim(),
      });

      setSubject('');
      setFirstMessage('');
      setSelectedTicket(ticket);
      setView('chat');
      fetchTickets();
      fetchMessages(ticket.id);
    } catch (err) {
      console.error(err);
    } finally {
      setSending(false);
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !selectedTicket) return;
    setSending(true);
    try {
      await supabase.from('ticket_messages').insert({
        ticket_id: selectedTicket.id,
        sender_id: user.id,
        is_admin: false,
        content: newMessage.trim(),
      });
      setNewMessage('');
    } catch (err) {
      console.error(err);
    } finally {
      setSending(false);
    }
  };

  const openTicket = (ticket: any) => {
    setSelectedTicket(ticket);
    setView('chat');
  };

  const formatTime = (d: string) => new Date(d).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });

  return (
    <>
      {/* Floating Button */}
      <button
        ref={toggleBtnRef}
        onClick={(e) => { e.stopPropagation(); setIsOpen(!isOpen); setUnread(0); }}
        className="fixed z-50 w-14 h-14 rounded-full bg-gradient-to-br from-accent to-accent-secondary text-accent-foreground flex items-center justify-center shadow-lg hover:shadow-xl transition-all hover:scale-105 bottom-6 right-6 lg:bottom-6 lg:right-6 max-lg:top-3 max-lg:right-3 max-lg:w-10 max-lg:h-10"
      >
        {isOpen ? <X size={22} className="lg:block hidden" /> : <MessageCircle size={22} className="lg:block hidden" />}
        {isOpen ? <X size={16} className="lg:hidden" /> : <MessageCircle size={16} className="lg:hidden" />}
        {unread > 0 && !isOpen && (
          <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-error text-white text-[10px] flex items-center justify-center font-bold">
            {unread}
          </span>
        )}
      </button>

      {/* Chat Panel */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            ref={panelRef}
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            className="fixed z-50 rounded-2xl border border-border-subtle bg-card shadow-2xl flex flex-col overflow-hidden bottom-24 right-6 w-[380px] h-[520px] max-lg:top-14 max-lg:right-2 max-lg:left-2 max-lg:w-auto max-lg:h-[70vh] max-lg:bottom-auto"
          >
            {/* Header */}
            <div className="px-4 py-3 border-b border-border-subtle bg-muted/30 flex items-center gap-3">
              {view !== 'list' && (
                <button onClick={() => { setView('list'); setSelectedTicket(null); }} className="text-muted-foreground hover:text-foreground">
                  <ArrowLeft size={16} />
                </button>
              )}
              <div className="flex-1">
                <p className="text-sm font-semibold text-foreground">
                  {view === 'list' ? 'Supporto' : view === 'new' ? 'Nuovo Ticket' : selectedTicket?.subject}
                </p>
                {view === 'list' && <p className="text-[10px] text-muted-foreground">Come possiamo aiutarti?</p>}
              </div>
            </div>

            {/* Content */}
            {view === 'list' && (
              <div className="flex-1 overflow-y-auto">
                <div className="p-3">
                  <Button variant="accent" size="sm" className="w-full" onClick={() => setView('new')}>
                    <Plus size={14} /> Nuovo Ticket
                  </Button>
                </div>
                {tickets.length === 0 ? (
                  <p className="text-center text-muted-foreground text-xs py-8">Nessun ticket. Creane uno nuovo!</p>
                ) : tickets.map((ticket) => (
                  <button
                    key={ticket.id}
                    onClick={() => openTicket(ticket)}
                    className="w-full text-left px-4 py-3 border-b border-border-subtle/50 hover:bg-muted/30 transition-colors"
                  >
                    <div className="flex items-center justify-between mb-1">
                      <p className="text-sm font-medium text-foreground truncate pr-2">{ticket.subject}</p>
                      <Badge
                        variant={ticket.status === 'open' ? 'warning' : ticket.status === 'resolved' ? 'success' : 'default'}
                        className="text-[10px] flex-shrink-0"
                      >
                        {statusLabels[ticket.status] || ticket.status}
                      </Badge>
                    </div>
                    <p className="text-[10px] text-muted-foreground">{formatTime(ticket.created_at)}</p>
                  </button>
                ))}
              </div>
            )}

            {view === 'new' && (
              <div className="flex-1 p-4 space-y-3">
                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">Oggetto</label>
                  <Input
                    placeholder="Descrivi brevemente il problema..."
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    className="bg-background"
                  />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">Messaggio</label>
                  <Textarea
                    placeholder="Spiega in dettaglio..."
                    value={firstMessage}
                    onChange={(e) => setFirstMessage(e.target.value)}
                    className="bg-background min-h-[120px]"
                  />
                </div>
                <Button
                  variant="accent"
                  className="w-full"
                  onClick={handleCreateTicket}
                  disabled={sending || !subject.trim() || !firstMessage.trim()}
                >
                  {sending ? 'Invio...' : 'Invia Ticket'}
                </Button>
              </div>
            )}

            {view === 'chat' && selectedTicket && (
              <>
                <div className="flex-1 overflow-y-auto p-3 space-y-2">
                  {messages.map((msg) => (
                    <div key={msg.id} className={`flex gap-2 ${msg.is_admin ? '' : 'flex-row-reverse'}`}>
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 text-[10px] ${
                        msg.is_admin ? 'bg-accent/20 text-accent' : 'bg-muted text-muted-foreground'
                      }`}>
                        {msg.is_admin ? <Shield size={10} /> : <User size={10} />}
                      </div>
                      <div className={`max-w-[75%] rounded-xl px-3 py-2 ${
                        msg.is_admin
                          ? 'bg-accent/10 border border-accent/20'
                          : 'bg-muted/50 border border-border-subtle'
                      }`}>
                        <p className="text-xs text-foreground whitespace-pre-wrap">{msg.content}</p>
                        <p className="text-[9px] text-muted-foreground mt-0.5">{formatTime(msg.created_at)}</p>
                      </div>
                    </div>
                  ))}
                  <div ref={messagesEndRef} />
                </div>
                <div className="border-t border-border-subtle p-2 flex gap-2">
                  <Input
                    placeholder="Scrivi un messaggio..."
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleSendMessage()}
                    className="bg-background text-xs h-9"
                  />
                  <Button variant="accent" size="sm" onClick={handleSendMessage} disabled={sending || !newMessage.trim()} className="h-9 w-9 p-0">
                    <Send size={14} />
                  </Button>
                </div>
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
